import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import {
  CheckCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useBackgroundTasks, type BackgroundTask } from '../lib/backgroundTasks';

function TaskRow({
  task,
  onRetry,
  onDismiss,
}: {
  task: BackgroundTask;
  onRetry: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  const isActive = task.status === 'pending' || task.status === 'running';
  const isError = task.status === 'error';
  const isSuccess = task.status === 'success';

  return (
    <div className={`bg-task-row bg-task-row--${task.status}`}>
      <div className="bg-task-row-main">
        <div className="bg-task-row-icon" aria-hidden>
          {isSuccess ? (
            <CheckCircleOutlined />
          ) : isError ? (
            <ExclamationCircleOutlined />
          ) : (
            <CloudUploadOutlined />
          )}
        </div>
        <div className="bg-task-row-copy">
          <div className="bg-task-row-title">{task.label}</div>
          <div className="bg-task-row-sub">
            {isError ? (
            <span className="bg-task-row-sub-error">{task.error}</span>
          ) : (
            task.progressLabel
          )}
          </div>
          {isActive && (
            <div className="bg-task-progress" aria-hidden>
              <div className="bg-task-progress-bar" style={{ width: `${task.progress}%` }} />
            </div>
          )}
        </div>
        <button
          type="button"
          className="bg-task-dismiss"
          onClick={() => onDismiss(task.id)}
          aria-label="Dismiss"
        >
          <CloseOutlined />
        </button>
      </div>
      <div className="bg-task-row-actions">
        {isError && (
          <Button type="link" size="small" icon={<ReloadOutlined />} onClick={() => onRetry(task.id)}>
            Retry
          </Button>
        )}
        {isSuccess && task.orderId && (
          <Link to={`/orders/${task.orderId}`} className="bg-task-view-link">
            View draft
          </Link>
        )}
      </div>
    </div>
  );
}

export function BackgroundTasksPanel() {
  const { tasks, retryTask, dismissTask, clearCompleted } = useBackgroundTasks();
  const [collapsed, setCollapsed] = useState(false);
  const prevActiveRef = useRef(0);

  const activeCount = useMemo(
    () => tasks.filter((t) => t.status === 'pending' || t.status === 'running').length,
    [tasks],
  );
  const errorCount = useMemo(() => tasks.filter((t) => t.status === 'error').length, [tasks]);

  useEffect(() => {
    if (activeCount > prevActiveRef.current) setCollapsed(false);
    prevActiveRef.current = activeCount;
  }, [activeCount]);

  if (tasks.length === 0) return null;

  const title =
    activeCount > 0
      ? `Saving ${activeCount} draft${activeCount !== 1 ? 's' : ''}…`
      : errorCount > 0
        ? `${errorCount} draft save${errorCount !== 1 ? 's' : ''} failed`
        : 'Draft saves complete';

  return (
    <div className={`bg-tasks-panel${collapsed ? ' bg-tasks-panel--collapsed' : ''}`}>
      <div className="bg-tasks-panel-header">
        <button
          type="button"
          className="bg-tasks-panel-toggle"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
        >
          <CloudUploadOutlined />
          <span>{title}</span>
          {collapsed ? <UpOutlined /> : <DownOutlined />}
        </button>
        {!collapsed && tasks.some((t) => t.status === 'success') && (
          <button type="button" className="bg-tasks-clear" onClick={clearCompleted}>
            Clear done
          </button>
        )}
      </div>
      {!collapsed && (
        <div className="bg-tasks-panel-body">
          {tasks.map((task) => (
            <TaskRow key={task.id} task={task} onRetry={retryTask} onDismiss={dismissTask} />
          ))}
        </div>
      )}
    </div>
  );
}
